import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  signal,
  Type,
  ViewContainerRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ButtonComponent } from '@examdojo/core/button';
import { NumberInputComponent } from '@examdojo/core/input';
import { JsonEditorComponent } from '@examdojo/core/json-editor';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { IonContent, IonFooter, IonHeader, IonModal, IonTitle, IonToggle, IonToolbar } from '@ionic/angular/standalone';
import { debounceTime, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  CUSTOM_COMPONENT_REGISTRY,
  provideCustomComponentRegistry,
  resolveComponentInputs,
} from '../../../custom-component-registry';
import { CustomComponentAlign, CustomComponentBlock } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { CustomComponentJsonEditorDirective } from './custom-component-json-editor.directive';
import {
  CUSTOM_COMPONENT_MAX_HEIGHT,
  CUSTOM_COMPONENT_MAX_WIDTH,
  CUSTOM_COMPONENT_MIN_HEIGHT,
  CUSTOM_COMPONENT_MIN_WIDTH,
  DEFAULT_CUSTOM_COMPONENT_PROPS,
} from './custom-component.model';

@Component({
  selector: 'dojo-custom-component-node',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonFooter,
    IonToggle,
    NumberInputComponent,
    ButtonComponent,
    JsonEditorComponent,
    CustomComponentJsonEditorDirective,
    MatButtonToggleModule,
  ],
  templateUrl: './custom-component.component.html',
  styleUrl: './custom-component.component.scss',
  hostDirectives: [DragWithHandleOnlyDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideCustomComponentRegistry(),
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => CustomComponentNodeComponent),
    },
  ],
})
export class CustomComponentNodeComponent extends NodeViewComponent<CustomComponentBlock> {
  constructor() {
    super();

    // Render the dynamic component when resolved component or inputs change
    effect(() => {
      const host = this.componentHost();
      const componentClass = this.resolvedComponent();
      const resolvedInputs = this.resolvedInputs();
      this.renderComponent(host ?? null, componentClass, resolvedInputs);
    });

    // Debounced subscription: Monaco editor → apply config
    this.configJsonCtrl.valueChanges
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .pipe(tap((value) => this.applyConfigJson(value)))
      .subscribe();
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly registry = inject(CUSTOM_COMPONENT_REGISTRY);
  private componentRef: ComponentRef<unknown> | null = null;

  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  private readonly componentHost = viewChild<unknown, ViewContainerRef>('componentHost', {
    read: ViewContainerRef,
  });

  readonly componentId = this.getProp('component_id');
  readonly preset = this.getProp('preset');
  readonly inputs = this.getProp('inputs');
  readonly width = this.getProp('width');
  readonly height = this.getProp('height');
  readonly padding = this.getProp('padding');
  readonly align = this.getProp('align');
  readonly showBorder = this.getProp('show_border');

  readonly registryEntry = computed(() => {
    const id = this.componentId();
    return id ? this.registry.get(id) ?? null : null;
  });

  readonly resolvedComponent = computed(() => this.registryEntry()?.component ?? null);

  readonly availablePresets = computed(() => {
    const entry = this.registryEntry();
    return entry?.presets ? Object.keys(entry.presets) : [];
  });

  readonly resolvedInputs = computed(() => {
    const entry = this.registryEntry();
    if (!entry) {
      return this.inputs();
    }
    return resolveComponentInputs(entry, this.preset(), this.inputs());
  });

  readonly containerStyle = computed(() => {
    const style: Record<string, string> = {};
    const w = this.width();
    const h = this.height();
    const p = this.padding();

    if (w !== null && w !== undefined) {
      style['width'] = `${w}px`;
    }
    if (h !== null && h !== undefined) {
      style['height'] = `${h}px`;
    }
    if (p !== null && p !== undefined) {
      style['padding'] = `${p}px`;
    }
    return style;
  });

  readonly wrapperStyle = computed(() => {
    const w = this.width();
    const a = this.align();
    if (w === null || w === undefined) {
      return {};
    }
    switch (a) {
      case 'center':
        return { 'justify-content': 'center' };
      case 'right':
        return { 'justify-content': 'flex-end' };
      default:
        return { 'justify-content': 'flex-start' };
    }
  });

  readonly hasFixedWidth = computed(() => this.width() !== null && this.width() !== undefined);
  readonly hasBorder = computed(() => this.showBorder() === true);

  readonly showSettings = signal(false);

  // Form controls for the settings dialog
  readonly widthCtrl = new FormControl<number | null>(null);
  readonly heightCtrl = new FormControl<number | null>(null);
  readonly paddingCtrl = new FormControl<number | null>(null);
  readonly configJsonCtrl = new FormControl<string>('', { nonNullable: true });

  override getDefaults = (): CustomComponentBlock => ({
    ...DEFAULT_CUSTOM_COMPONENT_PROPS,
    id: uuid(),
  });

  openSettings(): void {
    // Sync current values into form controls
    this.widthCtrl.setValue(this.width() ?? null);
    this.heightCtrl.setValue(this.height() ?? null);
    this.paddingCtrl.setValue(this.padding() ?? null);

    const config: Record<string, unknown> = {
      component_id: this.componentId(),
      inputs: this.inputs(),
    };
    const preset = this.preset();
    if (preset) {
      config['preset'] = preset;
    }
    this.configJsonCtrl.setValue(JSON.stringify(config, null, 2), { emitEvent: false });

    this.showSettings.set(true);
  }

  closeSettings(): void {
    this.showSettings.set(false);
  }

  applyWidth(): void {
    const val = this.widthCtrl.value;
    if (val !== null && val !== undefined && val >= CUSTOM_COMPONENT_MIN_WIDTH) {
      this.updateProp('width', val);
    } else {
      this.updateProp('width', null as unknown as undefined);
    }
  }

  applyHeight(): void {
    const val = this.heightCtrl.value;
    if (val !== null && val !== undefined && val >= CUSTOM_COMPONENT_MIN_HEIGHT) {
      this.updateProp('height', val);
    } else {
      this.updateProp('height', null as unknown as undefined);
    }
  }

  applyPadding(): void {
    const val = this.paddingCtrl.value;
    if (val !== null && val !== undefined && val >= 0) {
      this.updateProp('padding', val);
    } else {
      this.updateProp('padding', null as unknown as undefined);
    }
  }

  toggleBorder(checked: boolean): void {
    this.updateProp('show_border', checked);
  }

  setAlign(align: CustomComponentAlign): void {
    this.updateProp('align', align);
  }

  onWidthResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartX = event.clientX;
    const container = (event.target as HTMLElement).closest('.custom-component-container')!;
    this.resizeStartWidth = this.width() ?? container.clientWidth;
    const wrapperWidth = container.closest('.custom-component-wrapper')?.clientWidth ?? CUSTOM_COMPONENT_MAX_WIDTH;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - this.resizeStartX;
      const multiplier = this.align() === 'center' ? 2 : 1;
      const newWidth = Math.max(
        CUSTOM_COMPONENT_MIN_WIDTH,
        Math.min(wrapperWidth, this.resizeStartWidth + deltaX * multiplier),
      );
      this.updateProp('width', newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onHeightResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartY = event.clientY;
    this.resizeStartHeight =
      this.height() ?? (event.target as HTMLElement).closest('.custom-component-container')!.clientHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - this.resizeStartY;
      const newHeight = Math.max(
        CUSTOM_COMPONENT_MIN_HEIGHT,
        Math.min(CUSTOM_COMPONENT_MAX_HEIGHT, this.resizeStartHeight + deltaY),
      );
      this.updateProp('height', newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private applyConfigJson(value: string): void {
    try {
      const parsed = JSON.parse(value);
      const componentId = (parsed.component_id as string) ?? '';
      const inputs = (parsed.inputs as Record<string, unknown>) ?? {};
      const preset = parsed.preset as string | undefined;
      this.updateProps({
        component_id: componentId,
        inputs,
        preset: preset ?? (null as unknown as undefined),
      });
    } catch {
      // Invalid JSON — Monaco shows inline errors, no action needed
    }
  }

  private renderComponent(
    host: ViewContainerRef | null,
    componentClass: Type<unknown> | null,
    inputs: Record<string, unknown>,
  ): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }

    if (!host || !componentClass) {
      return;
    }

    host.clear();
    this.componentRef = host.createComponent(componentClass);

    if (inputs) {
      Object.entries(inputs).forEach(([key, value]) => {
        this.componentRef!.setInput(key, value);
      });
    }
  }
}
